import type { DisciplineDetails } from './interfaces'

import { useOptionsApi } from '@entities/viewer'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { useDisciplinePaymentLinksStore } from './payment-links/store'
import { useDisciplineStore } from './store'
import { useDisciplinePaymentProductsApi } from '../api/payment-products.hook'
import { abortDisciplinePaymentCalculateApi } from '../api/payment-calculate.hook'
import { useDisciplineContext } from './discipline-context.hook'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import {
  getDefaultPaymentOption,
  splitDisciplinePaymentProducts,
} from './discipline-payment-links'

interface DisciplinePaymentLinksReturn {
  close: () => Promise<void>
}

export const useDisciplinePaymentLinks = (): DisciplinePaymentLinksReturn => {
  const route = useRoute()
  const store = useDisciplinePaymentLinksStore()
  const disciplineStore = useDisciplineStore()
  const { whenReady } = useDisciplineContext()
  const { canManageOrders, canManageSubscriptions } = useDisciplinePrivileges()

  const setDefaultPaymentOption = async (details: DisciplineDetails): Promise<void> => {
    const customerId = details.customer?.id
    if (!customerId) return

    const products = await useDisciplinePaymentProductsApi({ customerId })
    const { oneTime } = splitDisciplinePaymentProducts(products ?? [], details.code)

    store.setPaymentOption(getDefaultPaymentOption(
      canManageSubscriptions.value,
      canManageOrders.value && oneTime.length > 0,
    ))
  }

  const init = (): void => {
    store.reset()

    // The discount percents are configuration: reread them when the page opens.
    void useOptionsApi()

    // The products depend on the customer, which the shared discipline read brings; the page loader covers both.
    void whenReady(route.params.id as string).then(() => {
      const details = disciplineStore.getDetails
      if (details) void setDefaultPaymentOption(details)
    })
  }

  const close = async (): Promise<void> => {
    await navigateTo(resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: route.params.id as string }) ?? '/')
  }

  onMounted(init)

  onBeforeUnmount(() => {
    abortDisciplinePaymentCalculateApi()
    store.reset()
  })

  return {
    close,
  }
}
